import style from '../../addons/mapStyle';

interface Mappable {
  markers?: MarkerItem[];
  showPublicTransport?: boolean;
  zoomMobile?: string;
  location: {
    lat: string;
    lng: string;
  };
  zoom: string;
  center: {
    lat: string;
    lng: string;
  };
  pin: {
    url: string;
    width: string;
    height: string;
  };
  makerurl: string;
}

interface MarkerItem {
  location: {
    lat: string;
    lng: string;
  };
  makerurl?: string;
  pin?: {
    url: string;
    width: string;
    height: string;
  };
}

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  panControl: false,
  rotateControl: false,
  scaleControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  scrollwheel: false,
  fullscreenControl: false,
  zoomControl: false,
  styles: style
};

const googleMapByElement = new WeakMap<HTMLElement, google.maps.Map>();
const DEFAULT_MARKER_COLOR = '7e1614';
// Teardrop-like marker shape to mimic Google default pin.
const DEFAULT_MARKER_PATH =
  'M18.4,2A14.318,14.318,0,0,0,4,16.76Q4,25.724,18.4,38,32.791,25.715,32.8,16.76A14.318,14.318,0,0,0,18.4,2Zm0,18A3.6,3.6,0,1,1,22,16.4,3.611,3.611,0,0,1,18.4,20Z';

class Map {
  private readonly mapDIV: HTMLElement;
  private googleMap!: google.maps.Map;
  private markerData!: Mappable;
  private zoomIn: HTMLElement | null = null;
  private zoomOut: HTMLElement | null = null;

  constructor(el: string | HTMLElement | null) {
    const div: HTMLElement | null =
      typeof el === 'string' ? document.querySelector<HTMLElement>(el) : el;
    const mapMarker = div?.dataset.mapMarker;
    if (!div || !mapMarker) {
      throw new Error('Map: element with data-map-marker required');
    }
    this.mapDIV = div;
    this.markerData = JSON.parse(mapMarker);
    this.init();
  }

  init(): void {
    const { lat, lng } = this.markerData.center;
    const center = new google.maps.LatLng(parseFloat(lat), parseFloat(lng));
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const defaultZoom = parseInt(this.markerData.zoom, 10);
    const mobileZoom = this.markerData.zoomMobile
      ? parseInt(this.markerData.zoomMobile, 10)
      : defaultZoom;
    const zoom = isMobile ? mobileZoom : defaultZoom;

    this.googleMap = new google.maps.Map(this.mapDIV, {
      center,
      zoom,
      ...mapOptions,
      styles: this.getMapStyles()
    });

    if (this.markerData.showPublicTransport) {
      const transitLayer = new google.maps.TransitLayer();
      transitLayer.setMap(this.googleMap);
    }

    googleMapByElement.set(this.mapDIV, this.googleMap);

    const mapWrap = this.mapDIV.closest('.map');
    this.zoomIn = mapWrap?.querySelector('.map__zoom--in') ?? null;
    this.zoomOut = mapWrap?.querySelector('.map__zoom--out') ?? null;

    this.addMarker();
    this.setupZoom();
  }

  private getMapStyles(): google.maps.MapTypeStyle[] {
    if (!this.markerData.showPublicTransport) return style;

    const transitOverrides: google.maps.MapTypeStyle[] = [
      {
        featureType: 'transit',
        elementType: 'labels.icon',
        stylers: [{ visibility: 'on' }]
      },
      {
        featureType: 'transit',
        elementType: 'labels.text.fill',
        stylers: [{ color: '#4e4e4e' }]
      },
      {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [{ color: '#8f8f8f' }, { visibility: 'on' }]
      },
      {
        featureType: 'transit.station',
        elementType: 'geometry',
        stylers: [{ color: '#d9d9d9' }, { visibility: 'on' }]
      }
    ];

    return [...style, ...transitOverrides];
  }

  private createMarker(
    markerItem: MarkerItem,
    fallbackPin?: Mappable['pin']
  ): void {
    const { lat, lng } = markerItem.location;
    const latLng = new google.maps.LatLng(parseFloat(lat), parseFloat(lng));
    const redirecturl = markerItem.makerurl || '';
    const pin = markerItem.pin || fallbackPin;
    const markerOptions: google.maps.MarkerOptions = {
      position: latLng,
      map: this.googleMap,
      animation: google.maps.Animation.DROP,
      optimized: false,
      clickable: true
    };

    // When no pin is provided, use a Google-style marker with project color.
    if (pin?.url) {
      markerOptions.icon = {
        url: pin.url,
        scaledSize: new google.maps.Size(
          parseInt(pin.width, 10),
          parseInt(pin.height, 10)
        )
      };
    } else {
      markerOptions.icon = {
        path: DEFAULT_MARKER_PATH,
        fillColor: `#${DEFAULT_MARKER_COLOR}`,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeOpacity: 0,
        strokeWeight: 0,
        scale: 0.9,
        anchor: new google.maps.Point(8, 15)
      };
    }

    const marker = new google.maps.Marker(markerOptions);

    if (redirecturl.length > 0) {
      google.maps.event.addListener(marker, 'click', (): void => {
        window.open(redirecturl, '_blank');
      });
    }
  }

  addMarker(): void {
    // New format: markers array (default Google marker icon unless pin is passed).
    if (
      Array.isArray(this.markerData.markers) &&
      this.markerData.markers.length > 0
    ) {
      this.markerData.markers.forEach((item: MarkerItem): void => {
        this.createMarker(item);
      });
      return;
    }

    // Backward compatible format: single marker with top-level fields.
    this.createMarker(
      {
        location: this.markerData.location,
        makerurl: this.markerData.makerurl
      },
      this.markerData.pin
    );
  }

  setupZoom(): void {
    if (this.zoomIn && this.zoomOut) {
      this.zoomIn.addEventListener('click', (e: Event): void => {
        e.preventDefault();
        this.googleMap.setZoom(this.googleMap.getZoom() + 1);
      });
      this.zoomOut.addEventListener('click', (e: Event): void => {
        e.preventDefault();
        this.googleMap.setZoom(this.googleMap.getZoom() - 1);
      });
    }
  }
}

/**
 * Initialize maps inside the active tab (or standalone maps not in tabs).
 * Skips hidden tabs until opened. Call again when tab changes.
 */
export function initMapsInActiveTab(): void {
  const tryInit = (): void => {
    if (typeof google === 'undefined' || !google.maps) {
      window.setTimeout(tryInit, 50);
      return;
    }

    document.querySelectorAll('[data-map-marker]').forEach(el => {
      const h = el as HTMLElement;
      const tabParent = el.closest('.tab__content');

      if (tabParent && !tabParent.classList.contains('isActive')) {
        return;
      }

      if (h.dataset.mapInitialized === 'true') {
        const gmap = googleMapByElement.get(h);
        if (gmap) {
          google.maps.event.trigger(gmap, 'resize');
        }
        return;
      }

      new Map(h);
      h.dataset.mapInitialized = 'true';
    });
  };

  tryInit();
}

export default Map;
